import { create } from '@bufbuild/protobuf';
import {
  CreateProductType,
  Reservoir,
  ReservoirSchema,
  Reservoir_BlueRoof,
  Reservoir_BlueRoofSchema,
  Reservoir_ConventionalRoof,
  Reservoir_ConventionalRoofSchema,
  Reservoir_CrateType,
  Reservoir_ConfigOptionsSchema,
  Reservoir_ConfigOptions_VolumeCalcStrategy,
  Reservoir_DryProtection,
  Reservoir_GreenType,
  Reservoir_GreenRoof,
  Reservoir_GreenRoofSchema,
  Reservoir_InfiltrationTank,
  Reservoir_InfiltrationTankSchema,
  Reservoir_Pond,
  Reservoir_PondSchema,
  Reservoir_Tank,
  Reservoir_TankSchema,
  Reservoir_Wadi,
  Reservoir_WadiSchema,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { TFunction } from 'i18next';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { CreateDrainModel } from '~/shared/models/system/CreateDrainModel';
import { CreateProductModel } from '~/shared/models/system/CreateProductModel';
import { IssueModel } from '~/shared/models/issues/IssueModel';
import {
  getRoundedMetricValue,
  roundToNDecimals,
} from '~/shared/models/metrics/metric-conversions';
import { WaterLevelsModel } from '~/shared/models/metrics/waterLevelsModel/WaterLevelsModel';
import { ProductModel } from '~/shared/models/system/ProductModel';
import { ReservoirFormValues } from '~/shared/models/system/ReservoirFormTypes';
import {
  asFloat,
  asInt,
  assertIsDefined,
  assertUnreachable,
} from '~/types/assert-type';
import { ReservoirParameterConfigModel } from '~/shared/models/system/ReservoirParameterConfigModel';
import { ReservoirTranslationsModel } from '~/shared/models/system/ReservoirTranslationsModel';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';

type ConventionalRoofProperties = Omit<Reservoir_ConventionalRoof, '$typeName'>;

type BlueRoofProperties = Omit<Reservoir_BlueRoof, '$typeName'>;

type GreenRoofProperties = Omit<Reservoir_GreenRoof, '$typeName'>;

type TankProperties = Omit<Reservoir_Tank, '$typeName'>;

type InfiltrationTankProperties = Omit<Reservoir_InfiltrationTank, '$typeName'>;

type PondProperties = Omit<Reservoir_Pond, '$typeName'>;

type WadiProperties = Omit<Reservoir_Wadi, '$typeName'>;

type TypeProperties = NonNullable<Reservoir['typeProperties']>;

export type ReservoirType = Exclude<
  TypeProperties extends { case: infer T } ? T : never,
  'sewer' | undefined
>;

export const reservoirCrateTypes = Object.values(Reservoir_CrateType).filter(
  (value) => typeof value === 'number',
);

export type VolumeCalcStrategy = Exclude<
  keyof typeof Reservoir_ConfigOptions_VolumeCalcStrategy,
  'UNSPECIFIED'
>;

export const volumeCalcStrategies: VolumeCalcStrategy[] = [
  'WATER_HEIGHT',
  'WATER_VOLUME',
  'WATER_AREA',
] as const;

export type ReservoirParameter =
  | keyof Pick<
      ReservoirFormValues,
      | 'title'
      | 'area'
      | 'areaGreen'
      | 'captureArea'
      | 'crateType'
      | 'factorHollow'
      | 'greenType'
      | 'drainageSpeed'
      | 'dryProtection'
      | 'externalUsage'
      | 'externalIrrigation'
      | 'maxWaterHeight'
      | 'minWaterHeight'
      | 'minWaterCapacityPercentage'
      | 'pondNapOffset'
      | 'volume'
      | 'volumeCalcStrategy'
      | 'targetWaterHeight'
      | 'targetWaterCapacityPercentage'
      | 'targetWaterHeightGrowingMonths'
      | 'targetWaterCapacityPercentageGrowingMonths'
      | 'type'
      | 'wadiMaxVolume'
    >
  | 'currentWaterLevel'
  | 'capacityUsedPercentage'
  | 'capacityUsedVolume';

export type EditableReservoirParameter = Exclude<
  ReservoirParameter,
  'currentWaterLevel' | 'capacityUsedPercentage' | 'capacityUsedVolume'
>;

type ReservoirPropertiesMap = {
  conventionalRoof: ConventionalRoofProperties;
  blueRoof: BlueRoofProperties;
  greenRoof: GreenRoofProperties;
  tank: TankProperties;
  infiltrationTank: InfiltrationTankProperties;
  pond: PondProperties;
  wadi: WadiProperties;
};

type ReservoirProperties<T extends ReservoirType> = {
  type: T;
} & ReservoirPropertiesMap[T];

type ReservoirFormTypeProperties = {
  [K in ReservoirType]: ReservoirProperties<K>;
}[ReservoirType];

export type ReservoirParameterDetails = {
  title: string;
  value: string | number;
  unit?: string;
  redirect?: boolean;
  big?: boolean;
  tooltip?: string;
};

export class ReservoirModel {
  readonly title: string;

  readonly id: string;

  readonly name: string;

  readonly maxWaterHeightMm: number;

  readonly maxWaterCapacityL: number;

  private minWaterHeightMm: number;

  private minWaterCapacityPercentage: number = 0;

  private targetWaterHeightMm: number;

  private targetWaterCapacityPercentage: number;

  private targetWaterHeightGrowingMonthsMm: number;

  private targetWaterCapacityPercentageGrowingMonths: number;

  private factorHollow: number;

  readonly products: ProductModel[];

  readonly sideLengthBlue: number;

  readonly areaBlueM2: number = 0;

  readonly type: ReservoirType;

  private typeProperties: ReservoirFormTypeProperties;

  readonly uiPosition: { x: number; y: number };

  readonly issues?: IssueModel[];

  private allowedCreateProductTypes: Set<CreateProductType>;

  private allowPressurePipe: boolean;

  readonly isInAdviceSystemState?: boolean;

  private allowDrain: boolean;

  readonly volumeCalcStrategy?: VolumeCalcStrategy;

  readonly totalCapacity: number;

  readonly defaultExternalIrrigation: number;

  constructor({
    reservoir,
    products,
    issues,
    isInAdviceSystemState,
  }: {
    reservoir: Reservoir;
    products: ProductModel[];
    issues?: IssueModel[];
    isInAdviceSystemState?: boolean;
  }) {
    this.id = ResourceNameIdConverter.reservoirNameToId(reservoir.name);
    this.title = reservoir.title;
    this.name = reservoir.name;
    this.maxWaterHeightMm = Number(reservoir.maxWaterHeightMm);

    this.maxWaterCapacityL = reservoir.maxWaterCapacityL;

    this.minWaterHeightMm = Number(reservoir.minWaterHeightMm);

    this.minWaterCapacityPercentage = Math.round(
      reservoir.minWaterCapacityPercentage,
    );

    this.targetWaterHeightMm = reservoir.targetWaterHeight;

    this.targetWaterCapacityPercentage = Math.round(
      reservoir.targetCapacityPercentage,
    );

    this.targetWaterHeightGrowingMonthsMm =
      reservoir.targetWaterHeightGrowingMonths;

    this.targetWaterCapacityPercentageGrowingMonths = Math.round(
      reservoir.targetCapacityPercentageGrowingMonths,
    );

    this.factorHollow = Math.round(reservoir.factorHollow * 100);

    this.sideLengthBlue = Math.sqrt(Number(reservoir.areaM2));

    this.areaBlueM2 = Number(reservoir.areaM2);

    this.typeProperties = ReservoirModel.createTypeProperties(
      reservoir.typeProperties,
    );

    this.type = this.typeProperties.type;

    this.volumeCalcStrategy = ReservoirModel.volumeCalcStrategyFromApi({
      type: this.type,
      volumeCalcStrategy: reservoir.configOptions?.volumeCalcStrategy,
    });

    assertIsDefined(reservoir.uiPosition);

    this.uiPosition = reservoir.uiPosition;

    this.products = products;

    this.issues = issues;

    this.allowedCreateProductTypes = new Set(
      reservoir.configOptions?.allowedCreateProductTypes ?? [],
    );

    this.allowPressurePipe =
      reservoir.configOptions?.allowPressurePipe ?? false;

    this.allowDrain = reservoir.configOptions?.allowGravitationalDrain ?? false;

    this.isInAdviceSystemState = isInAdviceSystemState;

    this.totalCapacity = this.maxWaterHeightMm * this.areaBlueM2 * 0.001;

    const DAYS_TO_REUSE_WATER = 14;

    const DAY_HOURS = 24;
    this.defaultExternalIrrigation = Math.round(
      reservoir.maxWaterCapacityL / (DAYS_TO_REUSE_WATER * DAY_HOURS),
    );
  }

  get areaGreenM2() {
    return this.typeProperties.type === 'greenRoof'
      ? this.typeProperties.areaGreen
      : 0;
  }

  get captureAreaM2() {
    return ReservoirModel.isTankType(this.type) &&
      'captureArea' in this.typeProperties
      ? this.typeProperties.captureArea
      : 0;
  }

  static getUseTargetPercentageByType(type: ReservoirType): boolean {
    return type === 'tank' || type === 'infiltrationTank';
  }

  static isTankType(type: ReservoirType): boolean {
    return type === 'tank' || type === 'infiltrationTank';
  }

  static getDefaultVolumeCalcStrategy(
    type: ReservoirType,
  ): VolumeCalcStrategy | undefined {
    return ReservoirModel.isTankType(type) ? 'WATER_VOLUME' : undefined;
  }

  static volumeCalcStrategyFromApi({
    type,
    volumeCalcStrategy,
  }: {
    type: ReservoirType;
    volumeCalcStrategy?: Reservoir_ConfigOptions_VolumeCalcStrategy;
  }): VolumeCalcStrategy | undefined {
    switch (volumeCalcStrategy) {
      case Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_HEIGHT:
        return 'WATER_HEIGHT';
      case Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_AREA:
        return 'WATER_AREA';
      case Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_VOLUME:
        return 'WATER_VOLUME';
      case Reservoir_ConfigOptions_VolumeCalcStrategy.UNSPECIFIED:
      case undefined:
        return ReservoirModel.getDefaultVolumeCalcStrategy(type);
      default:
        return assertUnreachable(volumeCalcStrategy);
    }
  }

  static volumeCalcStrategyToApi(
    volumeCalcStrategy: VolumeCalcStrategy,
  ): Reservoir_ConfigOptions_VolumeCalcStrategy {
    switch (volumeCalcStrategy) {
      case 'WATER_HEIGHT':
        return Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_HEIGHT;
      case 'WATER_VOLUME':
        return Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_VOLUME;
      case 'WATER_AREA':
        return Reservoir_ConfigOptions_VolumeCalcStrategy.WATER_AREA;
      default:
        return assertUnreachable(volumeCalcStrategy);
    }
  }

  static formatVolumeM3(volumeM3: number): string {
    return roundToNDecimals(volumeM3, 3).toString();
  }

  static targetVolumeToApi(
    formValues: Pick<
      ReservoirFormValues,
      'type' | 'volume' | 'volumeCalcStrategy'
    >,
  ): number | undefined {
    if (!ReservoirModel.isTankType(formValues.type)) {
      return undefined;
    }

    const volumeCalcStrategy =
      formValues.volumeCalcStrategy ??
      ReservoirModel.getDefaultVolumeCalcStrategy(formValues.type);

    return volumeCalcStrategy === 'WATER_VOLUME'
      ? undefined
      : asFloat(formValues.volume);
  }

  private toApiConfigOptions(volumeCalcStrategy: VolumeCalcStrategy) {
    return create(Reservoir_ConfigOptionsSchema, {
      allowPressurePipe: this.allowPressurePipe,
      allowGravitationalDrain: this.allowDrain,
      allowedCreateProductTypes: Array.from(this.allowedCreateProductTypes),
      volumeCalcStrategy:
        ReservoirModel.volumeCalcStrategyToApi(volumeCalcStrategy),
    });
  }

  private static createTypeProperties(
    typeProperties: Reservoir['typeProperties'],
  ): ReservoirFormTypeProperties {
    const typeCase = typeProperties.case;
    switch (typeCase) {
      case 'conventionalRoof':
        return {
          type: typeCase,
        };
      case 'blueRoof':
        return {
          type: typeCase,
          crateType: typeProperties.value.crateType,
        };
      case 'greenRoof':
        return {
          type: typeCase,
          areaGreen: typeProperties.value.areaGreen,
          greenType: typeProperties.value.greenType,
          crateType: typeProperties.value.crateType,
          externalIrrigation: typeProperties.value.externalIrrigation,
        };
      case 'tank':
        return {
          type: typeCase,
          captureArea: typeProperties.value.captureArea,
          dryProtection: typeProperties.value.dryProtection,
          externalUsage: typeProperties.value.externalUsage,
        };
      case 'infiltrationTank':
        return {
          type: typeCase,
          drainageSpeed: roundToNDecimals(
            typeProperties.value.drainageSpeed,
            2,
          ),
        };
      case 'pond':
        return {
          type: typeCase,
          napOffset: typeProperties.value.napOffset,
        };
      case 'wadi':
        return {
          type: typeCase,
          maxVolume: typeProperties.value.maxVolume,
          drainageSpeed: roundToNDecimals(
            typeProperties.value.drainageSpeed,
            2,
          ),
        };
      case 'sewer':
        throw new Error('Reservoir type sewer not expected');
      case undefined:
        throw new Error('Expected a reservoir type, but got undefined');
      default:
        return assertUnreachable(typeCase);
    }
  }

  // API TO FORM VALUES CONVERSION
  asFormValues(): ReservoirFormValues {
    return {
      area: this.areaBlueM2.toString(),
      type: this.typeProperties.type,
      areaGreen:
        'areaGreen' in this.typeProperties
          ? this.typeProperties.areaGreen.toString()
          : '0',
      dryProtection:
        'dryProtection' in this.typeProperties
          ? this.typeProperties.dryProtection.toString()
          : Reservoir_DryProtection.DISABLED.toString(),
      showExternalUsage:
        'externalUsage' in this.typeProperties
          ? !!this.typeProperties.externalUsage
          : false,
      externalUsage:
        'externalUsage' in this.typeProperties
          ? this.typeProperties.externalUsage.toString()
          : '0',
      captureArea:
        'captureArea' in this.typeProperties
          ? this.typeProperties.captureArea.toString()
          : '0',
      factorHollow: this.factorHollow.toString(),
      externalIrrigation:
        'externalIrrigation' in this.typeProperties
          ? this.typeProperties.externalIrrigation
          : false,
      greenType:
        'greenType' in this.typeProperties
          ? this.typeProperties.greenType.toString()
          : Reservoir_GreenType.DROUGHT_PROOF.toString(),
      drainageSpeed:
        'drainageSpeed' in this.typeProperties
          ? this.typeProperties.drainageSpeed.toString()
          : '0',
      maxWaterHeight: this.maxWaterHeightMm.toString(),
      volume: ReservoirModel.formatVolumeM3(this.maxWaterCapacityL / 1000),
      volumeCalcStrategy: this.volumeCalcStrategy ?? 'WATER_AREA',
      minWaterHeight: this.minWaterHeightMm.toString(),
      minWaterCapacityPercentage: this.minWaterCapacityPercentage.toString(),
      pondNapOffset:
        'napOffset' in this.typeProperties
          ? this.typeProperties.napOffset.toString()
          : '0',
      targetWaterHeight: this.targetWaterHeightMm.toString(),
      targetWaterHeightGrowingMonths:
        this.targetWaterHeightGrowingMonthsMm.toString(),
      targetWaterCapacityPercentage:
        this.targetWaterCapacityPercentage.toString(),
      targetWaterCapacityPercentageGrowingMonths:
        this.targetWaterCapacityPercentageGrowingMonths.toString(),
      title: this.title,
      wadiMaxVolume:
        'maxVolume' in this.typeProperties
          ? this.typeProperties.maxVolume.toString()
          : '0',
      crateType:
        'crateType' in this.typeProperties
          ? this.typeProperties.crateType.toString()
          : Reservoir_CrateType.WAVIN_AQUACELL_UP_85.toString(),
    };
  }

  // FORM VALUES TO API CONVERSION
  private static formValuesToApiTypeProperties(
    formValues: ReservoirFormValues,
  ) {
    switch (formValues.type) {
      case 'conventionalRoof':
        return {
          case: 'conventionalRoof' as const,
          value: create(Reservoir_ConventionalRoofSchema, {}),
        };
      case 'blueRoof':
        return {
          case: 'blueRoof' as const,
          value: create(Reservoir_BlueRoofSchema, {
            crateType: asInt(formValues.crateType),
          }),
        };
      case 'greenRoof':
        return {
          case: 'greenRoof' as const,
          value: create(Reservoir_GreenRoofSchema, {
            areaGreen: asFloat(formValues.areaGreen),
            greenType: asInt(formValues.greenType),
            crateType: asInt(formValues.crateType),
            externalIrrigation: formValues.externalIrrigation,
          }),
        };
      case 'tank':
        return {
          case: 'tank' as const,
          value: create(Reservoir_TankSchema, {
            dryProtection: asInt(formValues.dryProtection),
            externalUsage: formValues.showExternalUsage
              ? asInt(formValues.externalUsage)
              : 0,
            captureArea: asFloat(formValues.captureArea),
          }),
        };
      case 'infiltrationTank':
        return {
          case: 'infiltrationTank' as const,
          value: create(Reservoir_InfiltrationTankSchema, {
            drainageSpeed: asFloat(formValues.drainageSpeed),
          }),
        };
      case 'pond':
        return {
          case: 'pond' as const,
          value: create(Reservoir_PondSchema, {
            napOffset: asFloat(formValues.pondNapOffset),
          }),
        };
      case 'wadi':
        return {
          case: 'wadi' as const,
          value: create(Reservoir_WadiSchema, {
            maxVolume: asFloat(formValues.wadiMaxVolume),
            drainageSpeed: asFloat(formValues.drainageSpeed),
          }),
        };
      default:
        return assertUnreachable(formValues.type);
    }
  }

  toApiReservoir(formValues: ReservoirFormValues): Reservoir {
    const volumeCalcStrategy =
      formValues.volumeCalcStrategy ??
      ReservoirModel.getDefaultVolumeCalcStrategy(formValues.type);

    return create(ReservoirSchema, {
      name: this.name,
      uiPosition: this.uiPosition,
      typeProperties: ReservoirModel.formValuesToApiTypeProperties(formValues),
      configOptions:
        ReservoirModel.isTankType(formValues.type) && volumeCalcStrategy
          ? this.toApiConfigOptions(volumeCalcStrategy)
          : undefined,
      title: formValues.title,
      areaM2: BigInt(formValues.area),
      factorHollow: asInt(formValues.factorHollow) / 100,
      maxWaterHeightMm: BigInt(formValues.maxWaterHeight),
      minWaterHeightMm: BigInt(formValues.minWaterHeight),
      targetWaterHeight: asFloat(formValues.targetWaterHeight),
      targetWaterHeightGrowingMonths: asFloat(
        formValues.targetWaterHeightGrowingMonths,
      ),
      minWaterCapacityPercentage: asInt(formValues.minWaterCapacityPercentage),
      targetCapacityPercentage: asInt(formValues.targetWaterCapacityPercentage),
      targetCapacityPercentageGrowingMonths: asInt(
        formValues.targetWaterCapacityPercentageGrowingMonths,
      ),
    });
  }

  // SHOWABLE PARAMETERS
  static getUnitForParameter(
    parameter: ReservoirParameter,
    t: TFunction,
    type?: ReservoirType,
  ) {
    switch (parameter) {
      case 'currentWaterLevel':
      case 'minWaterHeight':
      case 'maxWaterHeight':
      case 'targetWaterHeight':
      case 'targetWaterHeightGrowingMonths':
        return 'mm';
      case 'area':
      case 'areaGreen':
      case 'captureArea':
        return 'm²';
      case 'volume':
        return 'm³';
      case 'minWaterCapacityPercentage':
      case 'targetWaterCapacityPercentage':
      case 'targetWaterCapacityPercentageGrowingMonths':
      case 'capacityUsedPercentage':
      case 'factorHollow':
        return '%';
      case 'drainageSpeed':
        return type === 'infiltrationTank'
          ? t('reservoirEdit.fields.units.drainageSpeed_infiltrationTank')
          : t('reservoirEdit.fields.units.drainageSpeed');
      case 'externalUsage':
        return t('reservoirEdit.fields.units.externalUsage');
      case 'capacityUsedVolume':
        return 'm³';
      case 'wadiMaxVolume':
        return 'l';
      case 'pondNapOffset':
        return 'm';
      default:
        return '';
    }
  }

  private getInfoForParameter(
    parameter: ReservoirParameter,
    t: TFunction,
    waterLevels?: WaterLevelsModel,
    showPredictions?: boolean,
  ): ReservoirParameterDetails {
    const typeForTooltip = ReservoirTranslationsModel.getTypeLabelForTooltip(
      this.type,
      t,
    );

    switch (parameter) {
      case 'area':
        return {
          title: t('reservoirEdit.fields.labels.area', {
            type:
              typeForTooltip.charAt(0).toUpperCase() + typeForTooltip.slice(1),
          }),
          value: this.areaBlueM2,
          unit: ReservoirModel.getUnitForParameter('area', t),
          tooltip: t('reservoirEdit.fields.tooltips.area', {
            type: typeForTooltip,
          }),
        };

      case 'minWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.minWaterHeight'),
          value: this.minWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('minWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.minWaterHeight', {
            type: typeForTooltip,
          }),
        };

      case 'minWaterCapacityPercentage':
        return {
          title: t('reservoirEdit.fields.labels.minWaterCapacityPercentage'),
          value: this.minWaterCapacityPercentage,
          unit: ReservoirModel.getUnitForParameter(
            'minWaterCapacityPercentage',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.minWaterCapacityPercentage',
          ),
        };

      case 'maxWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.maxWaterHeight'),
          value: this.maxWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('maxWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.maxWaterHeight', {
            type: typeForTooltip,
          }),
        };

      case 'targetWaterHeight':
        return {
          title: t('reservoirEdit.fields.labels.targetWaterHeight'),
          value: this.targetWaterHeightMm,
          unit: ReservoirModel.getUnitForParameter('targetWaterHeight', t),
          tooltip: t('reservoirEdit.fields.tooltips.targetWaterHeight', {
            type: typeForTooltip,
          }),
        };

      case 'targetWaterHeightGrowingMonths':
        return {
          title: t(
            'reservoirEdit.fields.labels.targetWaterHeightGrowingMonths',
          ),
          value: this.targetWaterHeightGrowingMonthsMm,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterHeightGrowingMonths',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterHeightGrowingMonths',
            {
              type: typeForTooltip,
            },
          ),
        };

      case 'targetWaterCapacityPercentage':
        return {
          title: t('reservoirEdit.fields.labels.targetWaterCapacityPercentage'),
          value: this.targetWaterCapacityPercentage,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterCapacityPercentage',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterCapacityPercentage',
          ),
        };

      case 'targetWaterCapacityPercentageGrowingMonths':
        return {
          title: t(
            'reservoirEdit.fields.labels.targetWaterCapacityPercentageGrowingMonths',
          ),
          value: this.targetWaterCapacityPercentageGrowingMonths,
          unit: ReservoirModel.getUnitForParameter(
            'targetWaterCapacityPercentageGrowingMonths',
            t,
          ),
          tooltip: t(
            'reservoirEdit.fields.tooltips.targetWaterCapacityPercentageGrowingMonths',
          ),
        };

      case 'factorHollow':
        return {
          title: t('reservoirEdit.fields.labels.factorHollow'),
          value: this.factorHollow,
          unit: ReservoirModel.getUnitForParameter('factorHollow', t),
          tooltip: t('reservoirEdit.fields.tooltips.factorHollow', {
            context:
              this.type === 'tank' || this.type === 'infiltrationTank'
                ? 'tank'
                : 'roof',
          }),
        };

      case 'title':
        return {
          title: t('reservoirEdit.fields.labels.reservoirTitle'),
          value: this.title,
        };

      case 'crateType':
        return {
          title: t('reservoirEdit.fields.labels.crateType'),
          value:
            'crateType' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForCrateType(
                  this.typeProperties.crateType,
                  t,
                )
              : '',
        };

      case 'greenType':
        return {
          title: t('reservoirEdit.fields.labels.greenType'),
          value:
            'greenType' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForGreenType(
                  this.typeProperties.greenType,
                  t,
                )
              : '',
        };

      case 'areaGreen':
        return {
          title: t('reservoirEdit.fields.labels.areaGreen'),
          value:
            'areaGreen' in this.typeProperties
              ? this.typeProperties.areaGreen
              : '',
          unit: ReservoirModel.getUnitForParameter('areaGreen', t),
          tooltip: t('reservoirEdit.fields.tooltips.areaGreen'),
        };

      case 'dryProtection':
        return {
          title: t('reservoirEdit.fields.labels.dryProtection'),
          value:
            'dryProtection' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForDryProtection(
                  this.typeProperties.dryProtection,
                  t,
                )
              : '',
          tooltip: t('reservoirEdit.fields.tooltips.dryProtection'),
        };

      case 'externalUsage':
        return {
          title: t('reservoirEdit.fields.labels.externalUsage'),
          value:
            'externalUsage' in this.typeProperties
              ? this.typeProperties.externalUsage
              : '',
          unit: ReservoirModel.getUnitForParameter('externalUsage', t),
          tooltip: t('reservoirEdit.fields.tooltips.externalUsage'),
        };

      case 'externalIrrigation':
        return {
          title: t('reservoirEdit.fields.labels.externalIrrigation'),
          value:
            'externalIrrigation' in this.typeProperties
              ? ReservoirTranslationsModel.getLabelForExternalIrrigation(
                  this.typeProperties.externalIrrigation,
                  t,
                )
              : '',
          tooltip: t('reservoirEdit.fields.tooltips.externalIrrigation'),
        };
      case 'captureArea':
        return {
          title: t('reservoirEdit.fields.labels.captureArea'),
          value:
            'captureArea' in this.typeProperties
              ? this.typeProperties.captureArea
              : '',
          unit: ReservoirModel.getUnitForParameter('captureArea', t),
          tooltip: t('reservoirEdit.fields.tooltips.captureArea'),
        };

      case 'drainageSpeed':
        return {
          title: t('reservoirEdit.fields.labels.drainageSpeed'),
          value:
            'drainageSpeed' in this.typeProperties
              ? this.typeProperties.drainageSpeed
              : '',
          unit: ReservoirModel.getUnitForParameter(
            'drainageSpeed',
            t,
            this.type,
          ),
          tooltip: t(
            this.type === 'infiltrationTank'
              ? 'reservoirEdit.fields.tooltips.drainageSpeed_infiltrationTank'
              : 'reservoirEdit.fields.tooltips.drainageSpeed',
            { type: typeForTooltip },
          ),
        };

      case 'pondNapOffset':
        return {
          title: t('reservoirEdit.fields.labels.pondNapOffset'),
          value:
            'napOffset' in this.typeProperties
              ? this.typeProperties.napOffset
              : '',
          unit: ReservoirModel.getUnitForParameter('pondNapOffset', t),
        };

      case 'wadiMaxVolume':
        return {
          title: t('reservoirEdit.fields.labels.wadiMaxVolume'),
          value:
            'maxVolume' in this.typeProperties
              ? this.typeProperties.maxVolume
              : '',
          unit: ReservoirModel.getUnitForParameter('wadiMaxVolume', t),
          tooltip: t('reservoirEdit.fields.tooltips.wadiMaxVolume'),
        };

      case 'volume':
        return {
          title: t('reservoirEdit.fields.labels.volume'),
          value: roundToNDecimals(this.maxWaterCapacityL / 1000, 3),
          unit: ReservoirModel.getUnitForParameter('volume', t),
          tooltip: t('reservoirEdit.fields.tooltips.volume', {
            type: typeForTooltip,
          }),
        };

      case 'currentWaterLevel':
        return {
          title: t('reservoirEdit.fields.labels.currentWaterLevel'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.WATER_HEIGHT_MM,
                value: waterLevels.getWaterLevelByReservoirName(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('currentWaterLevel', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'capacityUsedPercentage':
        return {
          title: t('reservoirEdit.fields.labels.capacityUsedPercentage'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.CALCULATED_WATER_PERCENTAGE,
                value: waterLevels.getCapacityUsedPercentage(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('capacityUsedPercentage', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'capacityUsedVolume':
        return {
          title: t('reservoirEdit.fields.labels.capacityUsedVolume'),
          value: waterLevels
            ? getRoundedMetricValue({
                metricType: MetricType.CALCULATED_WATER_VOLUME_M3,
                value: waterLevels.getCapacityUsedVolume(this.name) || 0,
              }) || 0
            : 0,
          unit: ReservoirModel.getUnitForParameter('capacityUsedVolume', t),
          redirect: !!showPredictions,
          big: true,
        };

      case 'type':
        return {
          title: t('reservoirEdit.fields.labels.type'),
          value: ReservoirTranslationsModel.getLabelForType(this.type, t),
        };

      case 'volumeCalcStrategy':
        throw new Error('volumeCalcStrategy is not a displayable parameter');
      default:
        return assertUnreachable(parameter);
    }
  }

  public getReservoirParameterDetails({
    t,
    waterLevels,
    includeAdvancedParameters = true,
    showPredictions,
  }: {
    t: TFunction;
    waterLevels?: WaterLevelsModel;
    includeAdvancedParameters?: boolean;
    showPredictions?: boolean;
  }): ReservoirParameterDetails[] {
    const parameters = ReservoirParameterConfigModel.getDisplayableParameters({
      reservoirType: this.type,
      includeAdvancedParameters,
      inAdviceState: this.isInAdviceSystemState,
    });

    return parameters.map((param) =>
      this.getInfoForParameter(param, t, waterLevels, showPredictions),
    );
  }

  public getReservoirParameterDetailsForReport(
    t: TFunction,
  ): ReservoirParameterDetails[] {
    const parameters =
      ReservoirParameterConfigModel.parametersForReport[this.type];

    return parameters.map((param) =>
      this.getInfoForParameter(param, t, undefined, false),
    );
  }

  public getProductCreationDetails(t: TFunction) {
    return Array.from(this.allowedCreateProductTypes).map((type) =>
      CreateProductModel.getCreateProductDetails(
        {
          type: type as Exclude<
            CreateProductType,
            | CreateProductType.CONTROLLER
            | CreateProductType.POLDER_VALVE
            | CreateProductType.VALVE
            | CreateProductType.PUMP
          >,
          reservoirName: this.name,
        },
        t,
      ),
    );
  }

  public getDrainCreationDetails(t: TFunction) {
    return [
      { enabled: this.allowDrain, pressurePipe: false },
      { enabled: this.allowPressurePipe, pressurePipe: true },
    ]
      .filter(({ enabled }) => enabled)
      .map(({ pressurePipe }) =>
        CreateDrainModel.getCreateDrainDetails(
          {
            pressurePipe,
            fromReservoirName: this.name,
          },
          t,
        ),
      );
  }

  get isRoofType(): boolean {
    return (
      this.type === 'blueRoof' ||
      this.type === 'conventionalRoof' ||
      this.type === 'greenRoof'
    );
  }

  public static shouldDisableHollowFactorField(
    crateType: Reservoir_CrateType,
  ): boolean {
    switch (crateType) {
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_160:
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_160_CAPILLARITY:
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_85:
      case Reservoir_CrateType.WAVIN_AQUACELL_UP_85_CAPILLARITY:
        return true;
      case Reservoir_CrateType.OTHER:
      case Reservoir_CrateType.UNSPECIFIED:
      default:
        return false;
    }
  }

  static usesAquacell160Crate(reservoir: ReservoirModel) {
    return (
      'crateType' in reservoir.typeProperties &&
      (reservoir.typeProperties.crateType ===
        Reservoir_CrateType.WAVIN_AQUACELL_UP_160 ||
        reservoir.typeProperties.crateType ===
          Reservoir_CrateType.WAVIN_AQUACELL_UP_160_CAPILLARITY)
    );
  }
}
